import { Paths, PathValue } from "@mctypes/global.types";
import React, { useEffect } from "react";
import { McInputNew, McTextAreaNew } from "../../../../../../../components";
import McDropdownNew from "../../../../../../../components/McDropdown/MCDropdownNew";
import { LuftmengdeMaalingerEnum } from "@mcapps/shared/gkReportConstants";
import { IGKLuftmengdemaalingResultater, IGKRapport } from "@mcapps/shared/reportSchemas/gkReportSchema";
import ReportDocxImageContainer from "../../../../ReportBoard/ReportDocxImageContainer";
import AnemometerTest from "./AnemometerTest";
import BalometerTest from "./BalometerTest";
import TilluftskanalTest from "./TilluftskanalTest";
import { beregnetLuftmengdeFraLuftskifter, beregnetVolum } from "./luftmengdeUtils";
import { Box, Flex, Paper, Stack, Text, Title } from "@mantine/core";
const LuftmengdeTestIRom: React.FC<{
  onUpdateEditData: <P extends Paths<IGKRapport, 4>>(path: P, value: PathValue<IGKRapport, P>) => void;
  data: IGKLuftmengdemaalingResultater;
}> = ({ data, onUpdateEditData }) => {
  const TILGJENGELIGELUFTMENGDEMAALINGER: LuftmengdeMaalingerEnum[] = [
    LuftmengdeMaalingerEnum.ANEMOMETERMAALING,
    LuftmengdeMaalingerEnum.TILLUFTSKANALMAALING,
    LuftmengdeMaalingerEnum.BALOMETERMAALING,
  ];
  useEffect(() => {
    utdataBeregninger();
  }, [data.inndata.romdimensjoner.romareal, data.inndata.romdimensjoner.romhoeyde]);
  const utdataBeregninger = () => {
    const romvolum = beregnetVolum(data.inndata.romdimensjoner.romareal, data.inndata.romdimensjoner.romhoeyde);
    const luftmengdeFraLuftskifter = beregnetLuftmengdeFraLuftskifter(romvolum, data.inndata.prosjektertLuftskifte);
    const utdata = {
      romvolum: romvolum,
      luftmengdeFraLuftskifter: luftmengdeFraLuftskifter,
    };
    onUpdateEditData("testResultater.luftmengdeMaalingIRom.utdata.felles", utdata);
  };
  return (
    <Paper withBorder p="sm" radius="sm" bg="#fdfdff">
      <Stack gap="xs">
        <Title order={4} fw={400}>
          Luftmengdemåling i rom
        </Title>
        <b>Hvilken type luftmengdemåling skal utføres?</b>
        <div style={{ width: "25%" }}>
          <McDropdownNew
            currentValue={data.inndata.valgtLuftmengdeMaalingsType}
            onChange={(_, item: LuftmengdeMaalingerEnum) =>
              onUpdateEditData("testResultater.luftmengdeMaalingIRom.inndata.valgtLuftmengdeMaalingsType", item)
            }
            defaultTitle="Hvilken type luftmengemåling skal utføres?"
            options={TILGJENGELIGELUFTMENGDEMAALINGER}
          />
        </div>
        {data.inndata.valgtLuftmengdeMaalingsType && (
          <>
            <Flex direction="column" mt="lg">
              <McInputNew
                label={
                  <>
                    {" "}
                    Fyll inn prosjektert luftmengde &#40;m<sup>3</sup>/t&#41;:{" "}
                  </>
                }
                value={data.inndata.prosjektertLuftmengde}
                normalizeNumbers={true}
                type="text"
                onBlur={(e) =>
                  onUpdateEditData(
                    "testResultater.luftmengdeMaalingIRom.inndata.prosjektertLuftmengde",
                    Number(e.target.value),
                  )
                }
                style={{ width: "25%" }}
              />
            </Flex>
            <Flex direction="column" mt="lg">
              <McInputNew
                label="Fyll inn prosjektert luftskifte i rommet (ACH):"
                value={data.inndata.prosjektertLuftskifte}
                normalizeNumbers={true}
                type="text"
                onBlur={(e) =>
                  onUpdateEditData(
                    "testResultater.luftmengdeMaalingIRom.inndata.prosjektertLuftskifte",
                    Number(e.target.value),
                  )
                }
                style={{ width: "25%" }}
              />
            </Flex>
            <Flex direction="column" mt="lg">
              <McInputNew
                label={
                  <>
                    {" "}
                    Fyll inn romareal &#40;m<sup>2</sup>&#41; og romhøyde &#40;m&#41;:{" "}
                  </>
                }
                placeholder="Romareal"
                value={data.inndata.romdimensjoner.romareal}
                normalizeNumbers={true}
                type="text"
                onBlur={(e) =>
                  onUpdateEditData(
                    "testResultater.luftmengdeMaalingIRom.inndata.romdimensjoner.romareal",
                    Number(e.target.value),
                  )
                }
                style={{ width: "25%" }}
              />
              <Box mt="0.25rem" style={{ width: "25%" }}>
                <McInputNew
                  placeholder="Romhøyde"
                  value={data.inndata.romdimensjoner.romhoeyde}
                  normalizeNumbers={true}
                  type="text"
                  onBlur={(e) =>
                    onUpdateEditData(
                      "testResultater.luftmengdeMaalingIRom.inndata.romdimensjoner.romhoeyde",
                      Number(e.target.value),
                    )
                  }
                  style={{ width: "100%" }}
                />
              </Box>
            </Flex>
            <Flex justify="space-between" mt="0.25rem">
              <Text mb={0}>
                Romvolum &#40;m<sup>3</sup>&#41; ={" "}
                <b>
                  {beregnetVolum(data.inndata.romdimensjoner.romareal, data.inndata.romdimensjoner.romhoeyde)?.toFixed(
                    2,
                  ) ?? ""}
                </b>
              </Text>
              <Text mb={0}>Formel: &#40;V = A * H&#41;</Text>
            </Flex>
            <hr style={{ marginTop: 0 }} />
            <Flex justify="space-between" mt="0.25rem">
              <Text mb={0}>
                Luftskifter til luftmengde &#40;m<sup>3</sup>/t&#41; ={" "}
                <b>
                  {beregnetLuftmengdeFraLuftskifter(
                    data.utdata.felles.romvolum,
                    data.inndata.prosjektertLuftskifte,
                  )?.toFixed(2) ?? ""}
                </b>
              </Text>
              <Text mb={0}>Formel: &#40;Q = V * ACH&#41;</Text>
            </Flex>
            <hr style={{ marginTop: 0 }} />
          </>
        )}
        {data.inndata.valgtLuftmengdeMaalingsType === LuftmengdeMaalingerEnum.ANEMOMETERMAALING && (
          <AnemometerTest onUpdateEditData={onUpdateEditData} data={data} />
        )}
        {data.inndata.valgtLuftmengdeMaalingsType === LuftmengdeMaalingerEnum.TILLUFTSKANALMAALING && (
          <TilluftskanalTest onUpdateEditData={onUpdateEditData} data={data} />
        )}
        {data.inndata.valgtLuftmengdeMaalingsType === LuftmengdeMaalingerEnum.BALOMETERMAALING && (
          <BalometerTest onUpdateEditData={onUpdateEditData} data={data} />
        )}
        {data.inndata.valgtLuftmengdeMaalingsType && (
          <Box mt="lg">
            <ReportDocxImageContainer docxImageTag="luftmengdeMaalingIRom" title="Målepunkter i rommet" />
            <div style={{ width: "50%" }}>
              <McTextAreaNew
                label="Kommentar:"
                value={data.inndata.kommentar}
                onChange={(e) =>
                  onUpdateEditData("testResultater.luftmengdeMaalingIRom.inndata.kommentar", e.target.value)
                }
              />
            </div>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};
export default LuftmengdeTestIRom;
